import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getDatabase, ref, get, set, remove, push, update } from 'firebase/database';
import { generateBaseSlug } from '../../utils/slugify';
import { getAuth } from 'firebase/auth';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  ChevronDown, ChevronUp, Search, Building2, MapPin, Mail, Globe,
  Facebook, Twitter, Linkedin, Instagram, Calendar, X, User,
  RefreshCw, Plus, ChevronLeft, ChevronRight, CheckCircle, EyeOff
} from 'lucide-react';
import { FaImage, FaTrash } from 'react-icons/fa';
import { app, storage } from '../../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const getExpirationBadge = (expirationDate) => {
  if (!expirationDate) return null;
  const now = new Date();
  const expiry = new Date(expirationDate);
  const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  if (daysLeft < 0) return { label: `Expired ${Math.abs(daysLeft)}d ago`, className: 'text-red-600 font-semibold' };
  if (daysLeft <= 7) return { label: `Expires in ${daysLeft}d`, className: 'text-orange-500 font-semibold' };
  return { label: expiry.toLocaleDateString(), className: 'text-gray-600' };
};

const LogoUpload = ({ currentLogo, onLogoChange }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const auth = getAuth();

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be less than 2MB'); return; }
    setUploading(true);
    try {
      const fileName = `${auth.currentUser.uid}-${Date.now()}-${file.name}`;
      const logoRef = storageRef(storage, `company-logos/${fileName}`);
      await uploadBytes(logoRef, file);
      const downloadURL = await getDownloadURL(logoRef);
      if (currentLogo && currentLogo.includes('company-logos')) {
        try { await deleteObject(storageRef(storage, currentLogo)); } catch {}
      }
      onLogoChange(downloadURL);
      toast.success('Logo uploaded successfully');
    } catch {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!currentLogo) return;
    try {
      if (currentLogo.includes('company-logos')) await deleteObject(storageRef(storage, currentLogo));
      onLogoChange('');
      toast.success('Logo removed successfully');
    } catch {
      toast.error('Failed to remove logo');
    }
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">Company Logo</label>
      <div className="flex items-start space-x-4">
        <div className="w-32 h-32 relative border rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
          {currentLogo ? (
            <img src={currentLogo} alt="Company logo" className="w-full h-full object-cover" />
          ) : (
            <FaImage className="w-8 h-8 text-gray-400" />
          )}
          {uploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          )}
        </div>
        <div className="space-y-2">
          <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept="image/*" className="hidden" />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="px-4 py-2 bg-blue-950 text-white rounded-md hover:bg-[#cddd3a] hover:text-blue-950 disabled:opacity-50 transition-colors duration-200"
          >
            {uploading ? 'Uploading...' : 'Upload Logo'}
          </button>
          {currentLogo && (
            <button
              type="button"
              onClick={handleRemoveLogo}
              disabled={uploading}
              className="px-4 py-2 flex items-center text-red-600 hover:text-red-700"
            >
              <FaTrash className="w-4 h-4 mr-2" />
              Remove Logo
            </button>
          )}
          <p className="text-sm text-gray-500">Recommended: Square image, max 2MB</p>
        </div>
      </div>
    </div>
  );
};

const AdminAddJob = ({ onClose, onJobAdded }) => {
  const db = getDatabase(app);
  const [loading, setLoading] = useState(false);
  const [existingEmployerEmails, setExistingEmployerEmails] = useState([]);
  const [emailSuggestionsOpen, setEmailSuggestionsOpen] = useState(false);
  const [formData, setFormData] = useState({
    jobTitle: '',
    description: '',
    companyName: '',
    companyLogo: '',
    parish: '',
    industry: '',
    degreeLevel: '',
    employmentType: 'Full-time',
    workType: 'On-site',
    experience: '',
    salary: '',
    otherRequirements: '',
    contactName: '',
    applicationEmail: '',
    website: '',
    expirationDays: '30',
    socialMedia: { facebook: '', twitter: '', linkedin: '', instagram: '' }
  });

  useEffect(() => {
    const fetchEmployerEmails = async () => {
      try {
        const snapshot = await get(ref(db, 'employers'));
        if (snapshot.exists()) {
          const emails = new Set();
          snapshot.forEach((child) => {
            const email = child.val()?.email;
            if (email) emails.add(email);
          });
          setExistingEmployerEmails(Array.from(emails));
        }
      } catch (err) {
        console.error('Error fetching existing employer emails:', err);
      }
    };
    fetchEmployerEmails();
  }, [db]);

  const typedEmail = formData.applicationEmail.trim().toLowerCase();
  const filteredEmailSuggestions = typedEmail
    ? existingEmployerEmails.filter((email) => email.toLowerCase().includes(typedEmail))
    : [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('socialMedia.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({ ...prev, socialMedia: { ...prev.socialMedia, [field]: value } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const functions = getFunctions(app);
      const adminCreateEmployerAndJob = httpsCallable(functions, 'adminCreateEmployerAndJob');
      const result = await adminCreateEmployerAndJob(formData);
      const { jobId, employerId, isNewAccount, ...jobData } = result.data;
      toast.success(
        isNewAccount
          ? 'Job published and employer account created. A setup email has been sent.'
          : 'Job published under the existing employer account.'
      );
      onJobAdded({ id: jobId, employerId, ...jobData });
      onClose();
    } catch (err) {
      console.error('Error publishing job:', err);
      toast.error('Error publishing job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center overflow-y-auto p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl mt-20 mb-8 relative">
        <div className="sticky top-0 bg-white p-6 border-b rounded-t-lg z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-blue-950">Add Job (Admin)</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X size={24} /></button>
          </div>
          <p className="text-sm text-gray-600 mt-2">Jobs created by admin are published immediately without review.</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <LogoUpload
            currentLogo={formData.companyLogo}
            onLogoChange={(url) => setFormData(prev => ({ ...prev, companyLogo: url }))}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Title *</label>
              <input type="text" name="jobTitle" required value={formData.jobTitle} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
              <input type="text" name="companyName" required value={formData.companyName} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea name="description" required value={formData.description} onChange={handleChange} rows={6}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parish *</label>
              <select name="parish" required value={formData.parish} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500">
                <option value="">Select Parish</option>
                {['Kingston','Saint Andrew','Saint Catherine','Clarendon','Manchester','Saint Elizabeth',
                  'Westmoreland','Saint James','Trelawny','Saint Ann','Saint Mary','Portland','Saint Thomas','Hanover','Remote']
                  .map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Industry *</label>
              <select name="industry" required value={formData.industry} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500">
                <option value="">Select Industry</option>
                {['Technology','Healthcare','Finance','Education','Manufacturing','Retail','Other']
                  .map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Degree Level *</label>
              <select name="degreeLevel" required value={formData.degreeLevel} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500">
                <option value="">Select Degree Level</option>
                {["None Required","High School/Equivalent","Associate's Degree","Bachelor's Degree",
                  "Master's Degree","Doctorate","Professional Certification"]
                  .map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Work Type *</label>
              <select name="workType" required value={formData.workType} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500">
                <option value="On-site">On-site</option>
                <option value="Remote">Remote</option>
                <option value="Hybrid">Hybrid</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type *</label>
              <select name="employmentType" required value={formData.employmentType} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500">
                <option value="full-time">Full Time</option>
                <option value="part-time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="internship">Internship</option>
                <option value="Volunteer">Volunteer</option>
                <option value="Freelance">Freelance</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Experience *</label>
              <select name="experience" required value={formData.experience} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500">
                <option value="">Select Experience Level</option>
                {["Entry Level","1+ years","2+ years","3+ years","5+ years","7+ years","10+ years","15+ years"]
                  .map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salary</label>
              <input type="text" name="salary" value={formData.salary} onChange={handleChange}
                placeholder="e.g. $50,000 - $70,000 per year"
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expires In (days) *</label>
              <input type="number" name="expirationDays" required min="1" max="365"
                value={formData.expirationDays} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Other Requirements</label>
            <textarea name="otherRequirements" value={formData.otherRequirements} onChange={handleChange} rows={4}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Employer Email *</label>
              <input type="email" name="applicationEmail" required value={formData.applicationEmail}
                onChange={handleChange}
                onFocus={() => setEmailSuggestionsOpen(true)}
                onBlur={() => setTimeout(() => setEmailSuggestionsOpen(false), 150)}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="employer@company.com" autoComplete="off" />
              {emailSuggestionsOpen && filteredEmailSuggestions.length > 0 && (
                <ul className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto bg-white border rounded-md shadow-lg">
                  {filteredEmailSuggestions.map((email) => (
                    <li key={email}>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setFormData(prev => ({ ...prev, applicationEmail: email }));
                          setEmailSuggestionsOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
                      >
                        {email}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
              <input type="text" name="contactName" value={formData.contactName} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <input type="url" name="website" value={formData.website} onChange={handleChange}
                placeholder="https://example.com"
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[['facebook','Facebook'],['twitter','Twitter'],['linkedin','LinkedIn'],['instagram','Instagram']]
              .map(([key, label]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input type="url" name={`socialMedia.${key}`} value={formData.socialMedia[key]} onChange={handleChange}
                    placeholder={`https://${key}.com/company`}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
              ))}
          </div>

          <div className="sticky bottom-0 bg-white pt-4 border-t">
            <div className="flex justify-end gap-4">
              <button type="button" onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={loading}
                className="px-6 py-2 bg-blue-950 text-white rounded-md hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? 'Publishing...' : 'Publish Job'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminEditJob = ({ job, onClose, onJobUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    jobTitle:         job.jobTitle || '',
    description:      job.description || '',
    companyName:      job.companyName || '',
    companyLogo:      job.companyLogo || '',
    parish:           job.parish || '',
    industry:         job.industry || '',
    degreeLevel:      job.degreeLevel || '',
    employmentType:   job.employmentType || 'Full-time',
    workType:         job.workType || 'On-site',
    experience:       job.experience || '',
    salary:           job.salary || '',
    otherRequirements:job.otherRequirements || '',
    contactName:      job.contactName || '',
    applicationEmail: job.applicationEmail || '',
    website:          job.website || '',
    socialMedia:      job.socialMedia || { facebook: '', twitter: '', linkedin: '', instagram: '' },
  });

  const db = getDatabase(app);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('socialMedia.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({ ...prev, socialMedia: { ...prev.socialMedia, [field]: value } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const updates = {
        ...formData,
        updatedAt: new Date().toISOString(),
      };

      // Regenerate slug if title or parish changed
      if (formData.jobTitle !== job.jobTitle || formData.parish !== job.parish) {
        const baseSlug = generateBaseSlug(formData.jobTitle, formData.parish);
        let slug = baseSlug;
        let counter = 2;
        while (true) {
          const snap = await get(ref(db, `jobSlugs/${slug}`));
          if (!snap.exists() || snap.val().jobId === job.id) break;
          slug = `${baseSlug}-${counter}`;
          counter++;
        }
        // Remove old slug index entry
        if (job.slug && job.slug !== slug) {
          await set(ref(db, `jobSlugs/${job.slug}`), null);
        }
        await set(ref(db, `jobSlugs/${slug}`), { employerId: job.employerId, jobId: job.id });
        updates.slug = slug;
      }

      await update(ref(db, `jobs/${job.employerId}/${job.id}`), updates);
      toast.success('Job updated successfully');
      onJobUpdated({ ...job, ...updates });
      onClose();
    } catch {
      toast.error('Error updating job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center overflow-y-auto p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl mt-20 mb-8 relative">
        <div className="sticky top-0 bg-white p-6 border-b rounded-t-lg z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-blue-950">Edit Job</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X size={24} /></button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <LogoUpload
            currentLogo={formData.companyLogo}
            onLogoChange={(url) => setFormData(prev => ({ ...prev, companyLogo: url }))}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Title *</label>
              <input type="text" name="jobTitle" required value={formData.jobTitle} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
              <input type="text" name="companyName" required value={formData.companyName} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea name="description" required value={formData.description} onChange={handleChange} rows={6}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parish *</label>
              <select name="parish" required value={formData.parish} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500">
                <option value="">Select Parish</option>
                {['Kingston','Saint Andrew','Saint Catherine','Clarendon','Manchester','Saint Elizabeth',
                  'Westmoreland','Saint James','Trelawny','Saint Ann','Saint Mary','Portland','Saint Thomas','Hanover','Remote']
                  .map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Industry *</label>
              <select name="industry" required value={formData.industry} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500">
                <option value="">Select Industry</option>
                {['Technology','Healthcare','Finance','Education','Manufacturing','Retail','Food & Restaurant','Tourism & Hospitality','Other']
                  .map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Degree Level *</label>
              <select name="degreeLevel" required value={formData.degreeLevel} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500">
                <option value="">Select Degree Level</option>
                {["None Required","High School/Equivalent","Associate's Degree","Bachelor's Degree",
                  "Master's Degree","Doctorate","Professional Certification"]
                  .map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Work Type *</label>
              <select name="workType" required value={formData.workType} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500">
                <option value="On-site">On-site</option>
                <option value="Remote">Remote</option>
                <option value="Hybrid">Hybrid</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type *</label>
              <select name="employmentType" required value={formData.employmentType} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500">
                <option value="full-time">Full Time</option>
                <option value="part-time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="internship">Internship</option>
                <option value="Volunteer">Volunteer</option>
                <option value="Freelance">Freelance</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Experience *</label>
              <select name="experience" required value={formData.experience} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500">
                <option value="">Select Experience Level</option>
                {["Entry Level","1+ years","2+ years","3+ years","5+ years","7+ years","10+ years","15+ years"]
                  .map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salary</label>
              <input type="text" name="salary" value={formData.salary} onChange={handleChange}
                placeholder="e.g. $50,000 - $70,000 per year"
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Other Requirements</label>
            <textarea name="otherRequirements" value={formData.otherRequirements} onChange={handleChange} rows={4}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Application Email *</label>
              <input type="email" name="applicationEmail" required value={formData.applicationEmail} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
              <input type="text" name="contactName" value={formData.contactName} onChange={handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <input type="url" name="website" value={formData.website} onChange={handleChange}
                placeholder="https://example.com"
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[['facebook','Facebook'],['twitter','Twitter'],['linkedin','LinkedIn'],['instagram','Instagram']]
              .map(([key, label]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input type="url" name={`socialMedia.${key}`} value={formData.socialMedia[key] || ''} onChange={handleChange}
                    placeholder={`https://${key}.com/company`}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
              ))}
          </div>

          <div className="sticky bottom-0 bg-white pt-4 border-t">
            <div className="flex justify-end gap-4">
              <button type="button" onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={loading}
                className="px-6 py-2 bg-blue-950 text-white rounded-md hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const JobModal = ({ job, isAdmin, onBackToReview, onUnlist, onEdit, onClose }) => {
  const expiryBadge = job.expirationDate ? getExpirationBadge(job.expirationDate) : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
          <h2 className="text-2xl font-bold text-blue-950">Job Details</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X size={24} /></button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-blue-950 mb-2">{job.jobTitle}</h3>
            <div className="flex items-center text-gray-600 mb-2">
              <Building2 className="h-4 w-4 mr-2" />{job.companyName}
            </div>
            <div className="flex items-center text-gray-600">
              <MapPin className="h-4 w-4 mr-2" />{job.parish}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            <span className="px-3 py-1 text-sm rounded-full bg-blue-100 text-blue-800">{job.employmentType}</span>
            <span className="px-3 py-1 text-sm rounded-full bg-purple-100 text-purple-800">{job.industry}</span>
            <span className="px-3 py-1 text-sm rounded-full bg-green-100 text-green-800">{job.experience}</span>
            {job.salary && (
              <span className="px-3 py-1 text-sm rounded-full bg-yellow-100 text-yellow-800">{job.salary}</span>
            )}
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-semibold mb-2">Job Description</h4>
            <p className="text-gray-700 whitespace-pre-wrap">{job.description}</p>
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-semibold mb-2">Requirements</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="font-medium">Education Level</p>
                <p className="text-gray-700">{job.degreeLevel}</p>
              </div>
              <div>
                <p className="font-medium">Experience Required</p>
                <p className="text-gray-700">{job.experience}</p>
              </div>
            </div>
            {job.otherRequirements && (
              <div className="mt-4">
                <p className="font-medium">Additional Requirements</p>
                <p className="text-gray-700">{job.otherRequirements}</p>
              </div>
            )}
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-semibold mb-4">Contact Information</h4>
            <div className="space-y-2">
              {job.applicationEmail && (
                <div className="flex items-center">
                  <Mail className="h-4 w-4 mr-2 text-gray-500" />
                  <a href={`mailto:${job.applicationEmail}`} className="text-blue-600 hover:text-blue-800">
                    {job.applicationEmail}
                  </a>
                </div>
              )}
              {job.website && (
                <div className="flex items-center">
                  <Globe className="h-4 w-4 mr-2 text-gray-500" />
                  <a href={job.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                    Company Website
                  </a>
                </div>
              )}
              {job.contactName && (
                <div className="flex items-center">
                  <User className="h-4 w-4 mr-2 text-gray-500" />
                  <span className="text-gray-700">{job.contactName}</span>
                </div>
              )}
            </div>
          </div>

          {(job.socialMedia?.facebook || job.socialMedia?.twitter || job.socialMedia?.linkedin || job.socialMedia?.instagram) && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-4">Social Media</h4>
              <div className="flex space-x-4">
                {job.socialMedia?.facebook && (
                  <a href={job.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                    <Facebook className="h-6 w-6" />
                  </a>
                )}
                {job.socialMedia?.twitter && (
                  <a href={job.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-600">
                    <Twitter className="h-6 w-6" />
                  </a>
                )}
                {job.socialMedia?.linkedin && (
                  <a href={job.socialMedia.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:text-blue-900">
                    <Linkedin className="h-6 w-6" />
                  </a>
                )}
                {job.socialMedia?.instagram && (
                  <a href={job.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-800">
                    <Instagram className="h-6 w-6" />
                  </a>
                )}
              </div>
            </div>
          )}

          <div className="mb-6 space-y-1">
            <div className="flex items-center text-gray-600">
              <Calendar className="h-4 w-4 mr-2" />
              Posted: {new Date(job.createdAt).toLocaleDateString()}
            </div>
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-gray-400" />
              <span className="mr-1 text-gray-600">Expires:</span>
              {expiryBadge ? (
                <span className={expiryBadge.className}>{expiryBadge.label}</span>
              ) : (
                <span className="text-gray-400 italic">Not set</span>
              )}
            </div>
          </div>

          {isAdmin && (
            <div className="mt-6 pt-6 border-t flex gap-3">
              <button
                onClick={() => onEdit(job)}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-950 text-white rounded-md hover:bg-blue-900 transition-colors"
              >
                Edit Job
              </button>
              <button
                onClick={() => onBackToReview(job)}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Send Back to Review
              </button>
              <button
                onClick={() => onUnlist(job)}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                <EyeOff className="w-4 h-4 mr-2" />
                Unlist
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const UnlistConfirmModal = ({ job, onConfirm, onCancel }) => {
  const [notifyEmployer, setNotifyEmployer] = useState(false);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="p-6">
          <h3 className="text-lg font-bold text-blue-950 mb-2">Unlist Job</h3>
          <p className="text-gray-600 mb-4">
            "{job.jobTitle}" will be removed from the site immediately. Job can be reactivated later.
          </p>
          <label className="flex items-center gap-2 text-sm text-gray-700 mb-6">
            <input
              type="checkbox"
              checked={notifyEmployer}
              onChange={(e) => setNotifyEmployer(e.target.checked)}
              className="h-4 w-4"
            />
            Send the employer a "your listing has been delisted" email
          </label>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(notifyEmployer)}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              Unlist Job
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AllJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedJobs, setExpandedJobs] = useState({});
  const [selectedJob, setSelectedJob] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAddJob, setShowAddJob] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [migratedJobIds, setMigratedJobIds] = useState(new Set());
  const [unlistTarget, setUnlistTarget] = useState(null);

  const db = getDatabase(app);
  const auth = getAuth(app);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!auth.currentUser) { setIsAdmin(false); return; }
      try {
        const snapshot = await get(ref(db, `admins/${auth.currentUser.uid}`));
        setIsAdmin(snapshot.exists());
      } catch {
        setIsAdmin(false);
      }
    };
    checkAdminStatus();
  }, [auth.currentUser, db]);

  const fetchJobs = useCallback(async () => {
    try {
      const snapshot = await get(ref(db, 'jobs'));
      if (snapshot.exists()) {
        const allJobs = [];
        Object.entries(snapshot.val()).forEach(([employerId, employerJobs]) => {
          Object.entries(employerJobs).forEach(([jobId, job]) => {
            if (job.status === 'active' || job.status === 'approved') {
              allJobs.push({ id: jobId, employerId, ...job });
            }
          });
        });
        allJobs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setJobs(allJobs);
      }
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Reset to page 1 when search or page size changes
  useEffect(() => { setCurrentPage(1); }, [searchTerm, pageSize]);

  const handleBackToReview = async (job) => {
    try {
      await set(ref(db, `pending-jobs/${job.employerId}/${job.id}`), {
        ...job, status: 'pending', movedToReviewAt: new Date().toISOString()
      });
      await remove(ref(db, `jobs/${job.employerId}/${job.id}`));
      setJobs(prev => prev.filter(j => j.id !== job.id));
      if (showModal && selectedJob?.id === job.id) { setShowModal(false); setSelectedJob(null); }
      toast.success('Job moved back to review successfully');
    } catch {
      toast.error('Error moving job back to review');
    }
  };

  const handleUnlistJob = async (job, notifyEmployer) => {
    try {
      const jobRef = ref(db, `jobs/${job.employerId}/${job.id}`);
      const now = new Date();

      await update(jobRef, {
        status: 'unlisted',
        unlistedAt: now.toISOString(),
        unlistedBy: 'admin',
        updatedAt: now.toISOString(),
      });

      setJobs(prev => prev.filter(j => j.id !== job.id));
      if (showModal && selectedJob?.id === job.id) { setShowModal(false); setSelectedJob(null); }
      setUnlistTarget(null);
      toast.success('Job unlisted successfully');

      if (notifyEmployer) {
        try {
          const functions = getFunctions(app);
          const notifyJobDelisted = httpsCallable(functions, 'notifyJobDelisted');
          await notifyJobDelisted({ ...job, id: job.id });
        } catch (notifyError) {
          console.error('Error sending delist notification email:', notifyError);
          toast.error('Job unlisted, but the notification email failed to send.');
        }
      }
    } catch {
      toast.error('Error unlisting job');
    }
  };

  const handleMigrateJob = async (job) => {
    if (!window.confirm(
      `Create an employer account for "${job.applicationEmail}" and link it to this job?\n\nThey will receive a setup email to activate their account.`
    )) return;
    try {
      const functions = getFunctions(app);
      const adminMigrateJobToEmployer = httpsCallable(functions, 'adminMigrateJobToEmployer');
      const result = await adminMigrateJobToEmployer({ jobId: job.id, currentEmployerId: job.employerId });
      const { newEmployerId } = result.data;
      setJobs(prev => prev.map(j =>
        j.id === job.id
          ? { ...j, employerId: newEmployerId, createdByAdmin: false }
          : j
      ));
      setMigratedJobIds(prev => new Set([...prev, job.id]));
      toast.success(`Employer account created for ${job.applicationEmail}. Setup email sent.`);
    } catch (err) {
      const message = err?.message || '';
      if (message.includes('already-exists') || message.includes('already exists')) {
        toast.error('An account with this email already exists. The employer can sign in with their existing credentials.');
      } else {
        toast.error('Failed to create employer account. Please try again.');
      }
    }
  };

  const toggleJobExpansion = (jobId) => {
    setExpandedJobs(prev => ({ ...prev, [jobId]: !prev[jobId] }));
  };

  const filteredJobs = jobs.filter(job => {
    const s = searchTerm.toLowerCase();
    return (
      job.jobTitle?.toLowerCase().includes(s) ||
      job.companyName?.toLowerCase().includes(s) ||
      job.description?.toLowerCase().includes(s) ||
      job.parish?.toLowerCase().includes(s) ||
      job.industry?.toLowerCase().includes(s)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / pageSize));
  const paginatedJobs = filteredJobs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (loading) return <div className="flex justify-center items-center h-96">Loading...</div>;

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-blue-950">All Jobs</h1>
        <div className="flex items-center gap-4">
          {isAdmin && (
            <button
              onClick={() => setShowAddJob(true)}
              className="flex items-center px-4 py-2 bg-blue-950 text-white rounded-md hover:bg-blue-900 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Job
            </button>
          )}
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search jobs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {filteredJobs.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <p className="text-gray-600">
            {searchTerm ? 'No jobs match your search criteria' : 'No jobs available'}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {paginatedJobs.map((job) => {
              const expiryBadge = job.expirationDate ? getExpirationBadge(job.expirationDate) : null;
              return (
                <div key={job.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h2 className="text-xl font-semibold text-blue-950">{job.jobTitle}</h2>
                        <div className="text-sm text-gray-600 mt-1">
                          {job.companyName} • {job.parish}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => { setSelectedJob(job); setShowModal(true); }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                          View Details
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => { setEditingJob(job); setShowModal(false); }}
                              className="px-4 py-2 bg-blue-950 text-white rounded-md hover:bg-blue-900 transition-colors"
                            >
                              Edit
                            </button>
                            {job.createdByAdmin && !migratedJobIds.has(job.id) && (
                              <button
                                onClick={() => handleMigrateJob(job)}
                                className="px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors flex items-center"
                                title={`Create employer account for ${job.applicationEmail}`}
                              >
                                <User className="w-4 h-4 mr-2" />
                                Create Employer Account
                              </button>
                            )}
                            {migratedJobIds.has(job.id) && (
                              <button
                                disabled
                                className="px-4 py-2 bg-green-100 text-green-700 rounded-md flex items-center cursor-default"
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Setup Email Sent
                              </button>
                            )}
                            <button
                              onClick={() => handleBackToReview(job)}
                              className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors flex items-center"
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Back to Review
                            </button>
                            <button
                              onClick={() => setUnlistTarget(job)}
                              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center"
                            >
                              <EyeOff className="w-4 h-4 mr-2" />
                              Unlist
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="p-4 pt-0">
                    <div className="flex flex-wrap gap-2 mb-2">
                      <span className="px-2 py-1 text-sm rounded-full bg-blue-100 text-blue-800">{job.employmentType}</span>
                      <span className="px-2 py-1 text-sm rounded-full bg-purple-100 text-purple-800">{job.industry}</span>
                      <span className="px-2 py-1 text-sm rounded-full bg-green-100 text-green-800">{job.experience}</span>
                    </div>

                    <div className="relative">
                      <div className={`prose max-w-none ${!expandedJobs[job.id] ? 'max-h-24 overflow-hidden' : ''}`}>
                        <p className="text-gray-700">{job.description}</p>
                      </div>
                      <button
                        onClick={() => toggleJobExpansion(job.id)}
                        className="text-blue-600 hover:text-blue-800 text-sm flex items-center mt-2"
                      >
                        {expandedJobs[job.id] ? (
                          <>Show Less <ChevronUp className="ml-1 h-4 w-4" /></>
                        ) : (
                          <>Show More <ChevronDown className="ml-1 h-4 w-4" /></>
                        )}
                      </button>
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-semibold">Required Education:</p>
                        <p className="text-gray-600">{job.degreeLevel}</p>
                      </div>
                      <div>
                        <p className="font-semibold">Salary:</p>
                        <p className="text-gray-600">{job.salary || 'Not specified'}</p>
                      </div>
                      <div>
                        <p className="font-semibold">Contact:</p>
                        <p className="text-gray-600">{job.applicationEmail}</p>
                      </div>
                      <div>
                        <p className="font-semibold">Posted:</p>
                        <p className="text-gray-600">{new Date(job.createdAt).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="font-semibold">Expires:</p>
                        {expiryBadge ? (
                          <p className={expiryBadge.className}>{expiryBadge.label}</p>
                        ) : (
                          <p className="text-gray-400 italic">Not set</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {(() => {
            const start = (currentPage - 1) * pageSize + 1;
            const end = Math.min(currentPage * pageSize, filteredJobs.length);
            const pages = [];
            for (let i = 1; i <= totalPages; i++) {
              if (i === 1 || i === totalPages || (i >= currentPage - 2 && i <= currentPage + 2)) {
                pages.push(i);
              } else if (pages[pages.length - 1] !== '...') {
                pages.push('...');
              }
            }
            return (
              <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Show:</span>
                  {PAGE_SIZE_OPTIONS.map(size => (
                    <button
                      key={size}
                      onClick={() => setPageSize(size)}
                      className={`px-3 py-1 rounded border text-sm font-medium ${pageSize === size ? 'bg-blue-950 text-white border-blue-950' : 'border-gray-300 hover:bg-gray-50'}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
                <p className="text-sm text-gray-600">
                  Showing {start}–{end} of {filteredJobs.length} jobs
                </p>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 rounded border text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    {pages.map((page, i) =>
                      page === '...' ? (
                        <span key={`ellipsis-${i}`} className="px-2 py-1 text-sm text-gray-400">…</span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-1.5 rounded border text-sm font-medium ${page === currentPage ? 'bg-blue-950 text-white border-blue-950' : 'border-gray-300 hover:bg-gray-50'}`}
                        >
                          {page}
                        </button>
                      )
                    )}
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 rounded border text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </>
      )}

      {showModal && selectedJob && (
        <JobModal
          job={selectedJob}
          isAdmin={isAdmin}
          onBackToReview={handleBackToReview}
          onUnlist={(job) => setUnlistTarget(job)}
          onEdit={(job) => { setEditingJob(job); setShowModal(false); setSelectedJob(null); }}
          onClose={() => { setShowModal(false); setSelectedJob(null); }}
        />
      )}

      {unlistTarget && (
        <UnlistConfirmModal
          job={unlistTarget}
          onConfirm={(notifyEmployer) => handleUnlistJob(unlistTarget, notifyEmployer)}
          onCancel={() => setUnlistTarget(null)}
        />
      )}

      {editingJob && (
        <AdminEditJob
          job={editingJob}
          onClose={() => setEditingJob(null)}
          onJobUpdated={(updated) => {
            setJobs(prev => prev.map(j => j.id === updated.id ? updated : j));
            setEditingJob(null);
          }}
        />
      )}

      {showAddJob && (
        <AdminAddJob
          onClose={() => setShowAddJob(false)}
          onJobAdded={(newJob) => setJobs(prev => [newJob, ...prev])}
        />
      )}
    </div>
  );
};

export default AllJobs;
